import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf } from '../skillUsage';

export const velocityMatchedCutSkill: CodeoSkill = {
    name: SKILL_NAMES.velocityMatchedCut,
    kind: 'codeo',
    description:
        'Make a hard swap between two on-screen states within a scene read as one continuous camera ' +
        "move by matching the outgoing exit's velocity to the incoming entrance's. For beats that feel jumpy.",
    documentUsesSkill: check(
        (script) =>
            /ease:\s*['"]power\d?\.in['"]/.test(script) &&
            /ease:\s*['"]power\d?\.out['"]/.test(script) &&
            countOf(script, /\by:\s*-?\d{2,}/g) >= 2,
    ),
    instructions: `# Velocity-Matched Cut

A swap feels jarring when the outgoing content stops dead and the incoming content starts from rest. Match the velocities across the swap instead: the outgoing content *accelerates* off-frame in the same direction the incoming content *decelerates* in from. The fastest point of both curves meets at the cut, so the eye reads one continuous camera move rather than two separate animations. It's the cheap polish that makes \`${SKILL_NAMES.momentSweepTransition}\` and plain content swaps both feel intentional. Like \`${SKILL_NAMES.momentSweepTransition}\`, both states already live in the same scene's DOM the whole time — this is a hard swap between them, not a change of scene.

## The principle

Exits use \`.in\` easing (accelerating); entrances use \`.out\` easing (decelerating); both move along the same axis in the same direction. Keep the entrance longer than the exit so the deceleration has room to settle.

## Markup

Both states share a grid cell so they land at the same spot. The incoming state starts at \`opacity: 0\` in CSS so it isn't visible before its entrance runs.

\`\`\`html
<div class="grid place-items-center" style="position: absolute; inset: 0;">
  <div id="state-a" class="text-center" style="grid-area: 1/1">
    <h1 id="state-a-line"
    style="margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 96px; color: var(--l5-color-0, #101820)">The old process</h1>
  </div>
  <div id="state-b" class="text-center" style="grid-area: 1/1; opacity: 0">
    <h1 id="state-b-line"
    style="margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 96px; color: var(--l5-color-1, #7a6dff)">One click</h1>
  </div>
</div>
\`\`\`

## Timeline code

At time \`T\`, state A exits up-and-accelerating while state B enters from below, decelerating:

\`\`\`js
const tl = gsap.timeline({ paused: true });
const T = 2.5, EXIT = 0.33;

// Outgoing — accelerate off the top of the frame
tl.to('#state-a', { y: -160, opacity: 0, duration: EXIT, ease: 'power2.in' }, T - EXIT);

// Incoming — start displaced in the SAME direction the exit was heading, decelerate to rest
tl.fromTo('#state-b', { y: 160, opacity: 0 },
    { y: 0, opacity: 1, duration: 1.0, ease: 'power2.out' }, T);

window.__timelines = { main: tl };
\`\`\`

Optional blur to sell the speed at the cut — a directional motion-blur on both halves, cleared as the incoming settles:

\`\`\`js
tl.to('#state-a', { filter: 'blur(12px)', duration: EXIT, ease: 'power2.in' }, T - EXIT);
tl.fromTo('#state-b', { filter: 'blur(12px)' }, { filter: 'blur(0px)', duration: 0.5, ease: 'power2.out' }, T);
\`\`\`

## Adapt

- **Match the ease families**: \`.in\` for exits, \`.out\` for entrances, same power (e.g. both \`power2\`). A \`power2.in\` exit into a \`power4.out\` entrance breaks the velocity match.
- **Same axis, same direction**: if the exit travels up (\`y: -160\`), the entrance must come from below (\`y: 160 → 0\`). Exit left → enter from the right. A direction flip reads as a bounce, not a continuation.
- **Distances roughly equal** so the speed at the cut is continuous; the entrance distance can be slightly larger to give the deceleration room.
- **Entrance longer than exit** (here 1.0s vs 0.33s) — the exit is a quick whip-off, the entrance a graceful settle.
- Combine with \`${SKILL_NAMES.cinematicCameraMove}\`: a velocity-matched swap between two states inside a continuous camera push feels like a single unbroken shot.

## Pitfalls

- Both states' incoming from-vars already carry \`opacity: 0\` (and \`#state-b\` starts at \`opacity: 0\` in CSS too), so default \`immediateRender\` already keeps everything hidden until it should show — don't add \`immediateRender: false\` to these tweens, it isn't needed once the from-state is already hidden.
- Keep blur modest (≤12px) and brief; heavy or lingering blur smears into mush and the H.264 encoder wastes bits on it.
- Don't velocity-match *every* beat — it implies continuous camera motion, so use it where content genuinely flows (setup→payoff); a hard topic change wants a real cut or a sweep.
- The moving content is GSAP-tweened on the y/opacity/filter channels — \`#state-a\`/\`#state-b\` carry no competing CSS \`transform\`, so there's nothing for those tweens to conflict with.
- Since both states already live in the scene's DOM for its whole length, there's no separate on-screen window to coordinate — just make sure \`T\` sits inside the timeline's own duration.`,
};
